import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { useMemo, useEffect, useCallback, useRef } from 'react';
import { matchPath, useHistory } from 'react-router-dom';
import { isEqual } from 'lodash';
import { useBiPageView } from 'seagull';
import { routes } from '~/constants';
import { useStore, useStoreStatic } from '~/contexts';
import { useEventReporter } from '~/event-reporter/EventReporter';
import { selectors } from '~/store/state-selectors';
export const usePageViewTracking = (param)=>{
    let { setPageBiData: setPageBiData } = param;
    const logBiPageView = useBiPageView();
    const { getState } = useStoreStatic();
    const { reportPageView } = useEventReporter();
    const lastSentBiRef = useRef();
    const history = useHistory();
    const hasUserInfo = useStore(selectors.user.hasUserInfo);
    const logPageViewBIEvent = useCallback((pageEvent)=>{
        if (isEqual(lastSentBiRef.current, pageEvent)) return;
        const { page_name } = pageEvent, data = _object_without_properties(pageEvent, [
            "page_name"
        ]);
        setPageBiData({
            pageName: page_name,
            pageEvent
        });
        if (!selectors.user.hasUserInfo(getState())) return;
        lastSentBiRef.current = pageEvent;
        logBiPageView({
            pageName: page_name,
            data: data
        });
    }, [
        logBiPageView,
        setPageBiData,
        getState
    ]);
    const pagesBiConfig = useMemo(()=>({
            login: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'login'
                    })
            },
            signUp: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'sign_up'
                    })
            },
            agreementStudio: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'agreement_studio'
                    })
            },
            redirect: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'redirect'
                    })
            },
            agreement: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { relationshipId: relationship_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'agreement',
                        relationship_id
                    });
                }
            },
            editAgreement: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { relationshipId: relationship_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'edit_agreement',
                        relationship_id
                    });
                }
            },
            proposalTemplateStudio: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'proposal_template_studio'
                    })
            },
            setPassword: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'set_password'
                    })
            },
            resetPassword: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'reset_password'
                    })
            },
            allSet: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { relationshipId: relationship_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'all_set',
                        relationship_id
                    });
                }
            },
            notificationAction: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'notification_action'
                    })
            },
            home: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'dashboard'
                    })
            },
            vendorOnboarding: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'vendor_onboarding'
                    })
            },
            acceptInvitation: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'accept_invitation'
                    })
            },
            invoices: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { relationshipId: relationship_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'agreement_invoices',
                        relationship_id
                    });
                }
            },
            billingHub: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { relationshipId: relationship_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'agreement_billing_hub',
                        relationship_id
                    });
                }
            },
            activityLog: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { relationshipId: relationship_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'agreement_activity_log',
                        relationship_id
                    });
                }
            },
            paymentMethods: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { relationshipId: relationship_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'agreement_payment_methods',
                        relationship_id
                    });
                }
            },
            draft: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { draftId: draft_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'proposal_draft',
                        draft_id
                    });
                }
            },
            proposalTemplate: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { templateId: template_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'proposal_template',
                        template_id
                    });
                }
            },
            clientOnboarding: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { relationshipId: relationship_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'client_onboarding',
                        relationship_id
                    });
                }
            },
            expiredProposalPage: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'expired_proposal_page'
                    })
            },
            canceledProposalPage: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'canceled_proposal_page'
                    })
            },
            noPermissionsRelationshipAlreadyApproved: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'no_permissions_relationship_already_approved'
                    })
            },
            allSignaturesAcquired: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { relationshipId: relationship_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'all_signatures_acquired',
                        relationship_id
                    });
                }
            },
            agreementsDashboard: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'agreements'
                    })
            },
            proposalsDashboard: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'proposals'
                    })
            },
            draftsDashboard: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'proposals_drafts'
                    })
            },
            archivedProposals: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'proposals_archived'
                    })
            },
            proposalTemplates: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'proposals_templates'
                    })
            },
            proposalDefaults: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'proposals_default_settings'
                    })
            },
            invoicesDashboard: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'invoices'
                    })
            },
            payoutsDashboard: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'payouts'
                    })
            },
            globalBillingHub: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'billing'
                    })
            },
            availableApps: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'available_apps'
                    })
            },
            connectedApps: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'connected_apps'
                    })
            },
            serviceLibrary: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'services'
                    })
            },
            bundlesLibrary: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'bundles'
                    })
            },
            importServices: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'services_import'
                    })
            },
            importContacts: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'contacts_import'
                    })
            },
            importAgreements: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'agreements_import'
                    })
            },
            anchorBilling: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'anchor_billing'
                    })
            },
            vendorDashboard: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'vendor_dashboard'
                    })
            },
            team: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'team'
                    })
            },
            businessDetails: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'business_details'
                    })
            },
            profile: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'profile'
                    })
            },
            notifications: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'notification_settings'
                    })
            },
            qboSetup: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'integration_setup_qbo'
                    })
            },
            xeroSetup: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'integration_setup_xero'
                    })
            },
            slackIntegration: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'integration_slack'
                    })
            },
            hubspotIntegration: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'integration_hubspot'
                    })
            },
            financialCentsSetup: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'integration_setup_financial_cents'
                    })
            },
            karbonSetup: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'integration_setup_karbon'
                    })
            },
            clientHubSetup: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'integration_setup_client_hub'
                    })
            },
            keeperSetup: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'integration_setup_keeper'
                    })
            },
            contacts: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'contacts'
                    })
            },
            clients: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'clients'
                    })
            },
            referrals: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'referrals'
                    })
            },
            kyb: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'kyb'
                    })
            },
            reports: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'reports'
                    })
            },
            revenueReport: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'reports_revenue_breakdown'
                    })
            },
            activeServicesReport: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'reports_active_services'
                    })
            },
            remainingCreditReport: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'reports_remaining_credit'
                    })
            },
            futureInvoicesReport: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'reports_future_invoices'
                    })
            },
            proposalsReport: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'reports_sent_proposals'
                    })
            },
            additionalPaymentProcessorInfo: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'additional_payment_processor_info'
                    })
            },
            anchorApiKeys: {
                logBI: ()=>logPageViewBIEvent({
                        page_name: 'api_keys'
                    })
            },
            clientOnboardingStepWelcome: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { relationshipId: relationship_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'client_onboarding_step_welcome',
                        relationship_id
                    });
                }
            },
            clientOnboardingStepSelectAPackage: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { relationshipId: relationship_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'client_onboarding_step_select_package',
                        relationship_id
                    });
                }
            },
            clientOnboardingStepReviewServices: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { relationshipId: relationship_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'client_onboarding_step_review_services',
                        relationship_id
                    });
                }
            },
            clientOnboardingStepApproveTerms: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { relationshipId: relationship_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'client_onboarding_step_approve_terms',
                        relationship_id
                    });
                }
            },
            clientOnboardingStepAccountantAccess: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { relationshipId: relationship_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'client_onboarding_step_accountant_access',
                        relationship_id
                    });
                }
            },
            clientOnboardingStepPaymentMethod: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { relationshipId: relationship_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'client_onboarding_step_payment_method',
                        relationship_id
                    });
                }
            },
            clientOnboardingStepIntroVideo: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { relationshipId: relationship_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'client_onboarding_step_intro_video',
                        relationship_id
                    });
                }
            },
            clientOnboardingStepAllSet: {
                logBI: (param)=>{
                    let { stateParams } = param;
                    const { relationshipId: relationship_id } = stateParams;
                    logPageViewBIEvent({
                        page_name: 'client_onboarding_step_all_set',
                        relationship_id
                    });
                }
            },
            activityLogs: {
                logBI: ()=>{
                    logPageViewBIEvent({
                        page_name: 'activity_logs'
                    });
                }
            },
            oauthAuthorize: {
                logBI: ()=>{
                    logPageViewBIEvent({
                        page_name: 'oauth_authorize'
                    });
                }
            }
        }), []);
    useEffect(()=>{
        const trackPageView = (location)=>{
            const queryParams = Object.fromEntries(new URLSearchParams(location.search));
            const page_name = location.pathname;
            let matchedRouteId = null;
            let stateParams = {};
            for (const [key, pathPattern] of Object.entries(routes)){
                if (!pathPattern) continue;
                const pattern = typeof pathPattern === 'function' ? pathPattern() : pathPattern;
                const result = matchPath(page_name, {
                    path: pattern,
                    exact: true
                });
                if (result) {
                    matchedRouteId = key;
                    stateParams = result.params;
                    break;
                }
            }
            const matchedRoute = matchedRouteId ? pagesBiConfig[matchedRouteId] : null;
            matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.logBI({
                stateParams,
                queryParams
            });
            if (matchedRouteId && hasUserInfo) {
                const worksAt = selectors.user.business(getState());
                reportPageView(location.pathname, matchedRouteId, worksAt);
            }
        };
        // Track the initial page view
        trackPageView(history.location);
        const unsubscribe = history.listen(trackPageView);
        return ()=>unsubscribe();
    }, [
        hasUserInfo
    ]);
};
